import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ConfirmDialogService } from '@serviceplan/ui-kit';
import { AdvertisingMaterialOptions, AudioTypeOptions, ClientNotificationHandlerService, FileObject, PROMPTS } from '@thehubz/client/core';
import { Asset, AssetsApiService } from '@thehubz/client/data-access-api';
import { FileUploadService } from '@thehubz/client/file-upload';
import { Subscription } from 'rxjs';
import { AssetUploadService } from '../../services/asset-upload.service';

@Component({
  selector: 'thehubz-upload-variants',
  templateUrl: './upload-variants.component.html',
  styleUrls: ['./upload-variants.component.scss'],
})
export class UploadVariantsComponent implements OnInit, OnDestroy {
  files: FileObject[] = [];

  editFlag = false;
  assetMediumOptions = AdvertisingMaterialOptions;
  audioTypeOptions = AudioTypeOptions;
  form!: UntypedFormGroup;
  progress = 0;
  currentUploadFileName = '';
  uploadInProgress = false;

  uploadStartSubscription?: Subscription;
  uploadCompleteSubscription?: Subscription;
  progressSubscription?: Subscription;

  AssetTypeEnum = Asset.AssetTypeEnum;

  constructor(
    private dialogRef: MatDialogRef<UploadVariantsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private fb: UntypedFormBuilder,
    private fileUploadService: FileUploadService,
    private clientNotificationHandlerService: ClientNotificationHandlerService,
    private assetUploadService: AssetUploadService,
    private assetsApiService: AssetsApiService,
    private confirmDialog: ConfirmDialogService,
  ) {}

  ngOnInit(): void {
    let assetMediumValue = '';
    let audioFile = '';
    if (this.data?.['formatMetadata']) {
      this.editFlag = true;
      assetMediumValue = this.data['formatMetadata']?.assetMedium || '';
      audioFile = this.data['formatMetadata']?.audioFile || '';
    }

    const aspectRation = 'aspect-' + this.data['aspectRatioCount'];
    this.form = this.fb.group({
      aspect_ratio: [aspectRation],
      assetMedium: [assetMediumValue],
      audioFile: [audioFile],
    });
    this.fileUploadsSubscription();
  }

  cancelUpload() {
    if (!this.form.pristine) {
      this.confirmDialog
        .open({
          title: $localize`Confirm`,
          message: $localize`Are you sure you want to close? All unsaved data will be lost`,
          confirmCaption: PROMPTS.LABELS.YES,
          cancelCaption: PROMPTS.LABELS.NO,
        })
        .subscribe((data) => {
          if (data) this.dialogRef.close();
        });
    } else {
      this.dialogRef.close();
    }
  }

  /*-------------------- File upload and assets creation funcs ---------------------*/

  updatedFilesList(event: any) {
    if (this.uploadInProgress) return;
    this.files = event.files;
  }

  selectedFiles(event: any) {
    this.files = event;
  }

  async onSubmit() {
    if (!this.form.valid) {
      return;
    }

    if (!this.editFlag) {
      this.dialogRef.disableClose = true;
      this.uploadInProgress = true;
      this.startUpload();
    } else {
      let formValues = this.form.getRawValue();
      const aspectRatioName = this.data['aspectRatio'].aspectRatio;
      const fileFormatName = this.data['formatMetadata'].fileNameRef;
      formValues = { ...formValues };
      delete formValues['aspect_ratio'];

      this.assetsApiService.assetsControllerUpdateFileFormatMetadata(this.data.assetId, aspectRatioName, fileFormatName, formValues).subscribe((res) => {
        this.dialogRef.close(res);
      });
    }
  }

  async startUpload() {
    try {
      for await (const fileObj of this.files) {
        if (!fileObj.uploaded) {
          const incompleteUpload = await this.assetUploadService.checkForIncompleteUploads(fileObj, this.files);
          if (typeof incompleteUpload === 'boolean') {
            const formValues = this.form.getRawValue();
            formValues.assetId = this.data.assetId;
            const asset = await this.assetUploadService.updateAspectRatio(fileObj, formValues);
            if (asset) {
              const key = FileUploadService.createAssetFileKeyForAspectRation(asset, formValues, fileObj);
              const fileIndex = this.files.findIndex((file) => file.name === fileObj.name);
              const file = this.files[fileIndex];
              this.files[fileIndex] = { ...file, key: key };
              await this.assetUploadService.uploadFile({ key: key, file: fileObj.file });
            }
          } else {
            await this.assetUploadService.uploadFile(incompleteUpload);
          }
        }
      }
    } catch (err) {
      this.onError(err);
    }
  }

  fileUploadsSubscription(): void {
    this.progressSubscription = this.fileUploadService.progress.subscribe((data) => {
      this.progress = data;
    });
    this.uploadStartSubscription = this.fileUploadService.fileUploadStartSubject.subscribe({
      next: (fileData) => {
        this.assetUploadService.updateFileDataOnStart(fileData, this.files);
      },
      error: (err) => {
        this.onError(err);
      },
    });
    this.uploadCompleteSubscription = this.fileUploadService.uploadedFileSubject.subscribe({
      next: (uploadedData) => {
        this.updateFileDataOnComplete(uploadedData);
      },
      error: (err) => {
        this.onError(err);
      },
    });
  }

  updateFileDataOnComplete(uploadedFileData: any): void {
    const fileIndex = this.files.findIndex((fileObj) => fileObj.key === uploadedFileData.key);
    const file = this.files[fileIndex];
    this.files[fileIndex] = { ...file, uploaded: true, uploading: false };
    if (this.files[fileIndex + 1]) {
      this.currentUploadFileName = this.files[fileIndex + 1].name;
      if (fileIndex + 1 < this.files.length && this.progress === 100) this.progress = 0;
    }
    if (fileIndex + 1 === this.files.length && this.progress === 100) {
      this.uploadInProgress = false;
      this.dialogRef.disableClose = false;
      this.dialogRef.close(true);
    }
  }

  onError(err: unknown) {
    this.uploadInProgress = false;
    this.dialogRef.disableClose = false;
    this.clientNotificationHandlerService.errorNotification(err);
  }

  ngOnDestroy(): void {
    this.progressSubscription?.unsubscribe();
    this.uploadStartSubscription?.unsubscribe();
    this.uploadCompleteSubscription?.unsubscribe();
  }
}
